import { useState } from 'react';
import { toast } from 'sonner';
import { useTranslations } from 'use-intl';
import { Mail, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { ServiceRegistry } from '@/services/ServiceRegistry';

interface ConnectGmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Company-scoped connect when set (company view); platform-wide connect when null
   * (platform-admin view) — mirrors the page's own current view mode rather than adding a
   * separate scope selector, per issue #290's "connect for this company" vs "connect
   * platform-wide" mental model. */
  companyId: string | null;
  companyName?: string | null;
}

/**
 * Entry point for connecting a Gmail mailbox (issue #290). Redirects the whole page to
 * Google's OAuth consent screen — there is nothing to render after `initiateGmailOAuth`
 * succeeds, the browser navigates away immediately.
 */
export function ConnectGmailDialog({ open, onOpenChange, companyId, companyName }: ConnectGmailDialogProps) {
  const t = useTranslations('consultantMessages.gmail');
  const [connecting, setConnecting] = useState(false);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const authUrl = await ServiceRegistry.getEmailThreadService().initiateGmailOAuth({
        connectionScope: companyId ? 'company_scoped' : 'platform_wide',
        companyId: companyId ?? undefined,
        // Google's return is handled server-side (gmail-oauth-callback), which sends the browser
        // back to whichever consultant deployment started the flow — identified by this origin.
        callbackUrl: `${window.location.origin}/gmail/callback`,
      });
      window.location.href = authUrl;
    } catch {
      toast.error(t('startFailed'));
      setConnecting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {t('title')}
          </DialogTitle>
          <DialogDescription>
            {companyId
              ? t.rich('companyBody', {
                  company: companyName ?? t('thisCompany'),
                  strong: (chunks) => <strong>{chunks}</strong>,
                })
              : t('platformBody')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={connecting}>
            {t('cancel')}
          </Button>
          <Button onClick={handleConnect} disabled={connecting}>
            {connecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {t('continue')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
